import * as React from 'react';
import { cn } from '../../lib';

/**
 * Entrada de dinero en **pesos colombianos enteros** (AGENTS §15: BIGINT en pesos, sin decimales).
 * El valor es un `bigint` de pesos; la vista agrupa miles con punto (`12.500.000`).
 */
function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function formatForInput(value: bigint): string {
  if (value <= 0n) return '';
  return groupThousands(value.toString());
}

function onlyDigits(input: string): string {
  return input.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '');
}

export interface CurrencyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: bigint;
  onChange: (value: bigint) => void;
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  description?: string;
  errorMessage?: string;
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, value, onChange, error, disabled, placeholder = '0', label, description, errorMessage, ...props }, ref) => {
    const displayValue = formatForInput(value);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = onlyDigits(e.target.value);
      onChange(digits ? BigInt(digits) : 0n);
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
        )}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50 pointer-events-none">
            $
          </span>
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={displayValue}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background pl-7 pr-3 py-2 text-sm text-right tabular-nums ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus-visible:ring-destructive',
              className
            )}
            aria-invalid={error}
            aria-label={label}
            {...props}
          />
        </div>
        {description && !errorMessage && (
          <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
        )}
        {errorMessage && (
          <p className="mt-1.5 text-sm text-destructive" role="alert">{errorMessage}</p>
        )}
      </div>
    );
  }
);
CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };
